import { t } from "@lingui/macro";
import {
  ArrowsLeftRightIcon,
  ArrowClockwiseIcon,
  CheckCircleIcon,
  CoinsIcon,
  CrownSimpleIcon,
  EyeIcon,
  InfoIcon,
  LightbulbIcon,
  SparkleIcon,
  TranslateIcon,
  WarningCircleIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { languageTrans } from "@reactive-resume/utils";
import { useEffect, useMemo, useState } from "react";

import { useStepFormResume } from "@/client/providers/step-form";
import { useAiWallet } from "@/client/services/ai-credit/api";
import { isAiInsufficientPointsError } from "@/client/services/ai/errors";
import { type AiQuote, type AiQuoteFeature, useAiQuote } from "@/client/services/ai/quote";
import { improveWriting } from "@/client/services/openai/improve-writing";
import { useTranslation } from "@/client/services/ollama/traduction-ai";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

type Tool = "suggestions" | "translation";

type TranslatedResume = {
  basics?: unknown;
  metadata?: unknown;
  sections?: unknown;
};

type QuoteCardProps = {
  quote: AiQuote;
  balance: number;
  pending: boolean;
  actionLabel: string;
  onConfirm: () => void;
  onRecharge: () => void;
};

const translatedSections = [
  "Coordonnées",
  "Expérience",
  "Formation",
  "Compétences",
  "Profil professionnel",
  "Langues",
];

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getErrorMessage = (error: unknown) => {
  const errorData = error as {
    response?: {
      data?: {
        message?: string;
        error?: string;
      };
    };
    message?: string;
  };

  return (
    errorData.response?.data?.message ??
    errorData.response?.data?.error ??
    errorData.message ??
    "Une erreur est survenue pendant le traitement IA."
  );
};

const QuoteCard = ({
  quote,
  balance,
  pending,
  actionLabel,
  onConfirm,
  onRecharge,
}: QuoteCardProps) => {
  const canAfford = quote.affordable || balance >= quote.points;

  const missingPoints = Math.max(0, quote.points - balance);

  return (
    <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-white p-2.5">
          <p className="text-[9px] font-semibold text-slate-400">Coût estimé</p>

          <strong className="mt-0.5 block text-sm text-[#071451]">{quote.points} points</strong>
        </div>

        <div className="rounded-lg bg-white p-2.5">
          <p className="text-[9px] font-semibold text-slate-400">Votre solde</p>

          <strong className="mt-0.5 block text-sm text-[#071451]">{balance} points</strong>
        </div>
      </div>

      <p className="mt-2 flex items-start gap-1.5 text-[9px] leading-4 text-indigo-700">
        <InfoIcon size={14} className="mt-0.5 shrink-0" />
        Le coût final sera ajusté selon les tokens réellement consommés.
      </p>

      <button
        type="button"
        disabled={pending}
        onClick={canAfford ? onConfirm : onRecharge}
        className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 px-4 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
      >
        {canAfford ? (
          <SparkleIcon size={16} weight="duotone" />
        ) : (
          <CoinsIcon size={16} weight="duotone" />
        )}

        {canAfford ? actionLabel : "Recharger mes crédits IA"}
      </button>

      {!canAfford && (
        <p className="mt-2 text-center text-[9px] leading-4 text-slate-500">
          Il vous manque {missingPoints.toFixed(2)} points pour continuer.
        </p>
      )}
    </div>
  );
};

export const AssistantPanel = ({ onPreview }: { onPreview: () => void }) => {
  const { selector } = useStepFormResume();

  const resume = useResumeStore((state) => state.resume);

  const setValue = useResumeStore((state) => state.setValue);

  const { user } = useUser();

  const wallet = useAiWallet();

  const quote = useAiQuote();

  const translation = useTranslation();

  const { open: openAiPacks } = useDialog("ai-packs");

  const finalization = selector === "finalization";

  const defaultLanguage =
    languageTrans.find((item) => item.key === "anglais")?.key ?? languageTrans[0]?.key ?? "anglais";

  const [tool, setTool] = useState<Tool>(finalization ? "translation" : "suggestions");

  const [language, setLanguage] = useState(defaultLanguage);

  const [suggestion, setSuggestion] = useState("");

  const [loadingSuggestion, setLoadingSuggestion] = useState(false);

  const [suggestionError, setSuggestionError] = useState("");

  const [translationApplied, setTranslationApplied] = useState(false);

  const summary = useMemo(
    () => stripHtml(String(resume.data.sections.summary.content ?? "")),
    [resume.data.sections.summary.content],
  );

  const currentBalance = wallet.data?.balance ?? quote.data?.balance ?? 0;

  const busy = loadingSuggestion || translation.isPending || quote.isPending;

  const translationFailed = translation.isError && !isAiInsufficientPointsError(translation.error);

  const selectTool = (nextTool: Tool) => {
    setTool(nextTool);
    quote.reset();
    setSuggestionError("");
  };

  useEffect(() => {
    if (finalization) {
      setTool("translation");

      quote.reset();
    }
  }, [finalization]);

  const requestSuggestionQuote = () => {
    if (!summary) {
      return;
    }

    setSuggestionError("");

    quote.mutate({
      feature: "improve",
      text: summary,
    });
  };

  const requestTranslationQuote = () => {
    quote.mutate({
      feature: "translate",
      resume: resume.data as Record<string, unknown>,
      language,
    });
  };

  const runSuggestion = async () => {
    if (!summary) {
      return;
    }

    setLoadingSuggestion(true);

    setSuggestionError("");

    try {
      const result = await improveWriting(summary);

      setSuggestion(result);

      quote.reset();

      void wallet.refetch();
    } catch (error) {
      if (isAiInsufficientPointsError(error)) {
        openRecharge("improve");
        return;
      }

      setSuggestionError(getErrorMessage(error));
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const runTranslation = () => {
    setTranslationApplied(false);

    translation.mutate(
      {
        resume: resume.data,
        language,
      },
      {
        onSuccess: () => {
          quote.reset();

          void wallet.refetch();
        },
        onError: (error) => {
          if (isAiInsufficientPointsError(error)) {
            openRecharge("translate");
          }
        },
      },
    );
  };

  const applySuggestion = () => {
    if (!suggestion) {
      return;
    }

    setValue("sections.summary.content", suggestion);
  };

  const applyTranslation = () => {
    const translated = translation.data as TranslatedResume | undefined;

    if (!translated?.basics || !translated.metadata || !translated.sections) {
      return;
    }

    /*
     * La traduction n’est appliquée
     * qu’après cette confirmation.
     *
     * Les appels à setValue passent
     * toujours par le store normal
     * du builder.
     */
    setValue("basics", translated.basics);

    setValue("metadata", translated.metadata);

    setValue("sections", translated.sections);

    setTranslationApplied(true);
  };

  const startNewTranslation = () => {
    translation.reset();
    quote.reset();

    setTranslationApplied(false);
  };

  const openRecharge = (feature: AiQuoteFeature) => {
    sessionStorage.setItem(`moncvpro:assistant-resume-after-topup:${resume.id}`, feature);

    openAiPacks("create", {
      id: "ai-packs",
      item: {
        type: feature,
      },
    });
  };

  /*
   * Après le paiement, payment.tsx
   * recharge actuellement la page.
   *
   * Le marqueur sessionStorage permet
   * de retrouver l’action qui était
   * en cours et de refaire son devis
   * avec le nouveau solde.
   */
  useEffect(() => {
    const storageKey = `moncvpro:assistant-resume-after-topup:${resume.id}`;

    const feature = sessionStorage.getItem(storageKey);

    if (!feature || quote.isPending || quote.data) {
      return;
    }

    sessionStorage.removeItem(storageKey);

    if (feature === "improve" && summary) {
      setTool("suggestions");

      quote.mutate({
        feature: "improve",
        text: summary,
      });

      return;
    }

    if (feature === "translate") {
      setTool("translation");

      quote.mutate({
        feature: "translate",
        resume: resume.data as Record<string, unknown>,
        language,
      });
    }
  }, [resume.id, summary, language, quote.isPending, quote.data]);

  return (
    <div className="h-full overflow-y-auto px-4 py-4 [scrollbar-color:#818cf8_transparent] [scrollbar-width:thin] sm:px-5">
      <header>
        <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-600">
          Rédaction et langues
        </p>

        <h2 className="mt-1 text-lg font-bold text-[#071451]">Assistant IA</h2>

        <p className="mt-1 text-[11px] leading-5 text-indigo-900/70">
          Améliorez vos textes ou traduisez votre CV avec une estimation claire avant chaque
          utilisation.
        </p>
      </header>

      <div
        role="tablist"
        aria-label="Outils de l’assistant IA"
        className="mt-4 grid grid-cols-2 gap-2"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tool === "suggestions"}
          onClick={() => {
            selectTool("suggestions");
          }}
          className={[
            "flex min-h-14 items-center justify-center gap-2 rounded-xl border px-2 text-[11px] font-bold transition",
            tool === "suggestions"
              ? "border-indigo-600 bg-indigo-50/50 text-indigo-700"
              : "border-slate-200 text-[#071451] hover:border-indigo-200",
          ].join(" ")}
        >
          <LightbulbIcon size={18} />
          Suggestions IA
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={tool === "translation"}
          onClick={() => {
            selectTool("translation");
          }}
          className={[
            "flex min-h-14 items-center justify-center gap-2 rounded-xl border px-2 text-[11px] font-bold transition",
            tool === "translation"
              ? "border-indigo-600 bg-indigo-50/50 text-indigo-700"
              : "border-slate-200 text-[#071451] hover:border-indigo-200",
          ].join(" ")}
        >
          <TranslateIcon size={18} />
          Traduction IA
        </button>
      </div>

      {tool === "suggestions" && (
        <section className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-bold text-[#071451]">Améliorer le profil professionnel</h3>

          <p className="mt-1 text-xs leading-5 text-slate-500">
            L’assistant reformule le profil présent dans votre CV sans l’appliquer automatiquement.
          </p>

          {summary ? (
            <div className="mt-3 rounded-xl bg-slate-50 p-3 text-[10px] leading-4 text-slate-600">
              <p className="line-clamp-4">{summary}</p>
            </div>
          ) : (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[10px] leading-4 text-amber-700">
              <WarningIcon size={16} className="mt-0.5 shrink-0" />
              Complétez d’abord la rubrique Profil professionnel.
            </div>
          )}

          {!quote.data && (
            <button
              type="button"
              disabled={!summary || busy}
              onClick={requestSuggestionQuote}
              className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-700 to-emerald-500 px-4 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CoinsIcon size={16} />

              {quote.isPending ? "Estimation en cours…" : "Estimer le coût de la suggestion"}
            </button>
          )}

          {quote.data?.feature === "improve" && (
            <QuoteCard
              quote={quote.data}
              balance={currentBalance}
              pending={busy}
              actionLabel="Générer la suggestion"
              onConfirm={() => {
                void runSuggestion();
              }}
              onRecharge={() => {
                openRecharge("improve");
              }}
            />
          )}

          {(quote.isError || suggestionError) && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-[10px] leading-4 text-rose-700">
              <WarningCircleIcon size={16} className="mt-0.5 shrink-0" />

              {suggestionError || getErrorMessage(quote.error)}
            </div>
          )}

          {loadingSuggestion && (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-indigo-50 p-3 text-[10px] font-semibold text-indigo-700">
              <SparkleIcon size={16} className="animate-pulse" />
              Génération de votre suggestion…
            </div>
          )}

          {suggestion && !loadingSuggestion && (
            <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-xs leading-5 text-indigo-950">
              <p>{suggestion}</p>

              <div className="mt-3 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={applySuggestion}
                  className="inline-flex items-center gap-2 font-bold text-emerald-600"
                >
                  <SparkleIcon size={15} />
                  Appliquer au CV
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setSuggestion("");

                    quote.reset();
                  }}
                  className="inline-flex items-center gap-2 font-bold text-indigo-600"
                >
                  <ArrowClockwiseIcon size={15} />
                  Nouvelle suggestion
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {tool === "translation" && (
        <div className="mt-4 space-y-3">
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-bold text-[#071451]">Traduire l’intégralité du CV</h3>

            <p className="mt-1 text-[10px] leading-4 text-indigo-900/70">
              La traduction est générée avant application. Vous gardez le contrôle sur le
              remplacement du contenu.
            </p>

            <div className="mt-4 grid items-end gap-3 sm:grid-cols-[1fr_auto_1fr]">
              <label className="text-[11px] font-bold text-[#071451]">
                Langue actuelle
                <select
                  disabled
                  className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold"
                >
                  <option>Français</option>
                </select>
              </label>

              <ArrowsLeftRightIcon
                size={24}
                className="mx-auto mb-2 hidden text-indigo-700 sm:block"
              />

              <label className="text-[11px] font-bold text-[#071451]">
                Traduire vers
                <select
                  value={language}
                  disabled={translation.isPending}
                  onChange={(event) => {
                    setLanguage(event.target.value);

                    quote.reset();
                    translation.reset();

                    setTranslationApplied(false);
                  }}
                  className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold outline-none focus:border-indigo-500"
                >
                  {languageTrans.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.value}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="p-4">
              <h3 className="text-xs font-bold text-[#071451]">Sections qui seront traduites</h3>

              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {translatedSections.map((section) => (
                  <span
                    key={section}
                    className="flex min-h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-[10px] font-semibold text-[#071451]"
                  >
                    <CheckCircleIcon
                      size={16}
                      weight="fill"
                      className="shrink-0 text-emerald-500"
                    />

                    {section}
                  </span>
                ))}
              </div>
            </div>

            <p className="flex items-start gap-2 border-t border-indigo-100 bg-indigo-50/70 px-4 py-3 text-[10px] font-medium leading-4 text-indigo-600">
              <InfoIcon size={17} className="mt-0.5 shrink-0" />
              Le modèle et la mise en page sont conservés. Le contenu ne change qu’après votre
              confirmation.
            </p>
          </section>

          {!quote.data && !translation.isPending && !translation.data && (
            <button
              type="button"
              disabled={quote.isPending}
              onClick={requestTranslationQuote}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 px-5 text-sm font-bold text-white shadow-lg disabled:opacity-40"
            >
              <CoinsIcon size={18} />

              {quote.isPending ? "Estimation en cours…" : "Estimer le coût de la traduction"}

              <span className="ml-auto grid size-5 place-items-center rounded bg-emerald-500 text-white">
                <CrownSimpleIcon size={11} weight="fill" />
              </span>
            </button>
          )}

          {quote.data?.feature === "translate" && !translation.data && (
            <QuoteCard
              quote={quote.data}
              balance={currentBalance}
              pending={busy}
              actionLabel="Confirmer et traduire mon CV"
              onConfirm={runTranslation}
              onRecharge={() => {
                openRecharge("translate");
              }}
            />
          )}

          {translation.isPending && (
            <div className="flex items-center gap-3 rounded-xl border border-indigo-100 bg-white p-4 text-xs font-semibold text-indigo-700">
              <span className="grid size-9 place-items-center rounded-full bg-indigo-50">
                <SparkleIcon size={17} className="animate-pulse" />
              </span>
              Traduction du CV en cours…
            </div>
          )}

          {(quote.isError || translationFailed) && (
            <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-[10px] leading-4 text-rose-700">
              <WarningCircleIcon size={16} className="mt-0.5 shrink-0" />

              {getErrorMessage(quote.isError ? quote.error : translation.error)}
            </div>
          )}

          {translation.data && !translation.isPending && (
            <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="flex items-start gap-2">
                <CheckCircleIcon
                  size={18}
                  weight="fill"
                  className="mt-0.5 shrink-0 text-emerald-500"
                />

                <div>
                  <h3 className="text-xs font-bold text-emerald-900">Traduction prête</h3>

                  <p className="mt-1 text-[10px] leading-4 text-emerald-700">
                    La traduction a été générée. Vous pouvez maintenant l’appliquer au CV ouvert.
                  </p>
                </div>
              </div>

              {!translationApplied ? (
                <button
                  type="button"
                  onClick={applyTranslation}
                  className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-xs font-bold text-white hover:bg-emerald-700"
                >
                  <SparkleIcon size={16} />
                  Appliquer la traduction au CV
                </button>
              ) : (
                <p className="mt-3 flex items-center gap-2 text-[10px] font-bold text-emerald-700">
                  <CheckCircleIcon size={16} weight="fill" />
                  Traduction appliquée au CV
                </p>
              )}

              <button
                type="button"
                onClick={startNewTranslation}
                className="mx-auto mt-3 flex items-center gap-2 text-[10px] font-bold text-indigo-600"
              >
                <ArrowClockwiseIcon size={14} />
                Nouvelle traduction
              </button>
            </section>
          )}

          <button
            type="button"
            onClick={onPreview}
            className="flex items-center justify-center gap-2 text-xs font-bold text-[#071451]"
          >
            <EyeIcon size={17} />
            {t`Afficher l’aperçu du CV`}
          </button>

          <p className="flex items-center justify-center gap-2 text-center text-[10px] font-medium text-amber-700">
            <WarningIcon size={15} className="shrink-0" />
            Relisez toujours la version traduite avant de la télécharger.
          </p>
        </div>
      )}
    </div>
  );
};
